import { get, isNil } from "lodash";
import { isNeedsPath } from "./needs-fields";
import { BINDING_KINDS, isFileSlot, setFieldLabel } from "./workshop-view";

export const CONTENT_MODES = { FIXED: "fixed", DATA: "data" };

export const NO_ACTION = "none";

export const CURRENT_SET_FIELD = "set-field:current";

export const searchable = (input, option) =>
  String(get(option, "label") || "")
    .toLowerCase()
    .includes(input.toLowerCase());

const isStorable = (field) =>
  field.mode === "WRITE" &&
  isNil(get(field, "target.execute")) &&
  isNil(get(field, "target.compute"));

const ATTACHMENT_ENTITY = "attachment";

const isAttachment = (field) =>
  get(field, "target.entity") === ATTACHMENT_ENTITY;

const fits = (field, { writableOnly, attachments }) =>
  (!writableOnly || isStorable(field)) &&
  (isNil(attachments) || isAttachment(field) === attachments);

const NEEDS_GROUP_KEY = "needs-group";

const groupNeeds = (options, { needsGroup, needsHint }) => {
  const needs = options.filter((option) => isNeedsPath(option.value));
  if (isNil(needsGroup) || !needs.length) return options;
  return [
    {
      key: NEEDS_GROUP_KEY,
      label: needsGroup,
      options: isNil(needsHint)
        ? needs
        : needs.map((option) => ({
            ...option,
            description: needsHint(option.value),
          })),
    },
    ...options.filter((option) => !isNeedsPath(option.value)),
  ];
};

export const fieldOptions = (
  registry,
  { writableOnly, attachments, keep, needsGroup, needsHint } = {},
) =>
  groupNeeds(
    (registry || []).flatMap((entity) =>
      (entity.fields || [])
        .map((field) => ({ field, path: `${entity.key}.${field.key}` }))
        .filter(
          ({ field, path }) =>
            path === keep || fits(field, { writableOnly, attachments }),
        )
        .map(({ field, path }) => ({
          value: path,
          label: field.label ? `${path} — ${field.label}` : path,
        })),
    ),
    { needsGroup, needsHint },
  );

const kindOf = (slot) => get(slot, "binding.kind");

const setFieldOf = (slot) => {
  const field = `${get(slot, "binding.entity")}.${get(slot, "binding.field")}`;
  const value = get(slot, "binding.value");
  return { field, value: isNil(value) ? "" : String(value) };
};

const switchOf = (slot, switches) => {
  if (kindOf(slot) !== BINDING_KINDS.SET_FIELD) return null;
  const setField = setFieldOf(slot);
  return (
    switches.find(
      (item) =>
        !isNil(item.setField) &&
        item.setField.field === setField.field &&
        item.setField.value === setField.value,
    ) || null
  );
};

export const registryFieldOf = (registry, path) => {
  const [entity, ...rest] = String(path || "").split(".");
  const group = (registry || []).find((item) => item.key === entity);
  return get(group, "fields", []).find((item) => item.key === rest.join("."));
};

export const fitsSlot = (registry, slot, path) => {
  const field = registryFieldOf(registry, path);
  return isNil(field) || isAttachment(field) === isFileSlot(slot);
};

export const contentModeOf = (slot) =>
  kindOf(slot) === BINDING_KINDS.DATA
    ? CONTENT_MODES.DATA
    : CONTENT_MODES.FIXED;

export const buttonTextOf = (slot) => {
  const source = get(slot, "binding.sourceValue");
  if (typeof source === "string") return source;
  const label = get(slot, "label") || "";
  return label === get(slot, "binding.name") ? "" : label;
};

export const buttonChoices = ({ slot, actions, switches, labels, known }) => {
  const kind = kindOf(slot);
  const currentSwitch = switchOf(slot, switches);
  const otherSetField =
    kind === BINDING_KINDS.SET_FIELD && isNil(currentSwitch);
  const journeyOptions = actions.map((action) => ({
    value: action.name,
    label: get(labels, ["TRIGGERS", action.name]) || action.name,
    description: action.description,
  }));
  return {
    selected:
      (kind === BINDING_KINDS.PROCESS_TRIGGER
        ? get(slot, "binding.name")
        : null) ||
      get(currentSwitch, "value") ||
      (otherSetField ? CURRENT_SET_FIELD : null),
    keptSetField: otherSetField ? setFieldOf(slot) : null,
    catalogAction: kind === BINDING_KINDS.CATALOG_ACTION,
    options: [
      { value: NO_ACTION, label: labels.NO_ACTION, description: "" },
      ...(otherSetField
        ? [
            {
              value: CURRENT_SET_FIELD,
              label: setFieldLabel(slot.binding, labels, known || {}),
              description: "",
            },
          ]
        : []),
      ...(switches.length
        ? [
            { label: labels.SHOW_STATES, options: switches },
            { label: labels.JOURNEY_ACTIONS, options: journeyOptions },
          ]
        : journeyOptions),
    ],
  };
};
